import { useMemo, type ReactNode } from 'react';
import { Avatar, Pill, type PillTone } from '../../design-system/primitives';
import type { FieldRef } from '../assembly';
import { fieldOf, fieldStates, formatValue, isAmountLike, type FieldSummary, type ObjectSummary } from '../format';
import { useImageLinks } from '../image-links';
import { labelOfRecord, shortId, useReferenceIndex } from '../reference-labels';
import { useAppServices } from '../ViewContext';
import { RowActionCell } from './RowActionCell';
import type { CollectionState, Row } from './useCollection';

/**
 * Rendu d'une valeur dans une collection (ADR-094, ADR-096) : le même pour la table, les cartes, le
 * Kanban et l'Agenda. Statut en pastille au ton déclaré (libellé toujours visible), référence par
 * son libellé lu sous l'autorisation de l'utilisateur, site par son nom, champ masqué dit « Masqué ».
 */

export type Column = FieldRef;
type Tone = NonNullable<Column['tones']>[string];

const TONE_TO_PILL: Readonly<Record<Tone, PillTone>> = { ok: 'success', warn: 'warn', danger: 'danger', info: 'info', neutral: 'neutral' };
/** Champs d'enveloppe triables (ADR-009). */
export const ENVELOPE_SORTABLE = new Set(['created_at', 'updated_at']);

/**
 * Format déclaré d'une colonne (core-component-inputs 1.7.0) : forme d'une date et décimales d'un nombre ou d'une mesure.
 * `undefined` quand il ne s'applique pas (valeur d'un autre type, montant : R2) ; le rendu par défaut prend alors le relais.
 */
export function formatColumnValue(format: Column['format'], value: unknown, unit?: string, locale = 'fr-FR', now: Date = new Date()): string | undefined {
  if (format === undefined) return undefined;
  if (format.date !== undefined && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const at = new Date(value);
    if (Number.isNaN(at.getTime())) return undefined;
    if (format.date === 'date') return at.toLocaleDateString(locale);
    if (format.date === 'datetime') return at.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
    const minutes = Math.round((at.getTime() - now.getTime()) / 60_000);
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (Math.abs(minutes) < 60) return relative.format(minutes, 'minute');
    if (Math.abs(minutes) < 60 * 24) return relative.format(Math.round(minutes / 60), 'hour');
    return relative.format(Math.round(minutes / (60 * 24)), 'day');
  }
  if (format.decimals !== undefined) {
    const isMeasure = value !== null && typeof value === 'object' && typeof (value as { value?: unknown }).value === 'number' && typeof (value as { unit?: unknown }).unit === 'string';
    const number = typeof value === 'number' ? value : isMeasure ? (value as { value: number }).value : undefined;
    if (number === undefined) return undefined;
    const text = number.toLocaleString(locale, { minimumFractionDigits: format.decimals, maximumFractionDigits: format.decimals });
    const shownUnit = isMeasure ? (value as { unit: string }).unit : unit;
    return shownUnit === undefined ? text : `${text} ${shownUnit}`;
  }
  return undefined;
}

export function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter((part) => part !== '');
  return (parts.length === 0 ? '?' : parts.length === 1 ? parts[0]!.slice(0, 2) : `${parts[0]![0]}${parts[parts.length - 1]![0]}`).toUpperCase();
}

export interface CellRenderer {
  /** Texte d'une valeur (référence par libellé, énumération par libellé, montant formaté…). */
  display(fieldId: string, row: Row): string;
  /** Valeur riche : pastille de statut, cellule image + titre, site, masquage. */
  cell(column: Column, row: Row): ReactNode;
  /** URL signée de l'image d'un champ (ADR-103) : un champ fichier de la ligne, ou l'image déclarée de l'objet cité par une référence. */
  image(fieldId: string, row: Row): string | undefined;
  alignOf(column: Column): 'start' | 'center' | 'end';
  classOf(column: Column): string;
}

/**
 * Libellés des références et des sites pour les champs montrés, puis le rendu. `fields` : tous les
 * champs que la représentation affiche (colonnes, champs de carte, titre…), pour une lecture par type.
 */
export function useCellRenderer(object: ObjectSummary | undefined, columns: readonly Column[], fields: readonly string[], rows: readonly Row[], meta: CollectionState['meta']): CellRenderer {
  const services = useAppServices();
  const shown = [...new Set([...columns.map((column) => column.field), ...fields])];
  const byType = new Map<string, string[]>();
  for (const fieldId of shown) {
    const field = fieldOf(object, fieldId);
    if (field?.type !== 'reference' || field.reference_type === undefined) continue;
    const ids = byType.get(field.reference_type) ?? [];
    for (const row of rows) if (typeof row[fieldId] === 'string') ids.push(row[fieldId] as string);
    byType.set(field.reference_type, ids);
  }
  const references = useReferenceIndex(services.client, [...byType.entries()].map(([type, ids]) => ({ type, ids })));
  const labels = references.labels;
  // Fichiers à lire : les champs fichier montrés de la ligne, puis l'image des objets cités.
  const fileOf = (fieldId: string, row: Row): string | undefined => {
    const field = fieldOf(object, fieldId);
    const value = row[fieldId];
    if (typeof value !== 'string') return undefined;
    if (field?.type === 'file') return value;
    if (field?.type === 'reference') return references.images.get(value);
    return undefined;
  };
  const wantedFiles: string[] = [];
  for (const fieldId of shown) for (const row of rows) {
    const file = fileOf(fieldId, row);
    if (file !== undefined) wantedFiles.push(file);
  }
  const links = useImageLinks(services.client, wantedFiles);
  const image = (fieldId: string, row: Row): string | undefined => {
    const file = fileOf(fieldId, row);
    return file === undefined ? undefined : links.get(file);
  };
  const entities = useMemo(
    () => new Map((Array.isArray(services.context['entities']) ? (services.context['entities'] as Array<{ entity_id?: unknown; label?: unknown }>) : []).filter((entry) => typeof entry.entity_id === 'string' && typeof entry.label === 'string').map((entry) => [entry.entity_id as string, entry.label as string])),
    [services.context],
  );
  const states = fieldStates(meta);

  const display = (fieldId: string, row: Row): string => {
    const field: FieldSummary | undefined = fieldOf(object, fieldId);
    const value = row[fieldId];
    if (states.masked.has(fieldId)) return 'Masqué';
    if (fieldId === 'entity_id' && typeof value === 'string') return entities.get(value) ?? shortId(value);
    if (field?.type === 'reference' && typeof value === 'string') return labels.get(value) ?? shortId(value);
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && field?.type === 'json') return labelOfRecord(value as Record<string, unknown>) ?? formatValue(value, field);
    return formatValue(value, field);
  };

  const cell = (column: Column, row: Row): ReactNode => {
    const value = row[column.field];
    if (states.masked.has(column.field)) return <span className="ay-masked">Masqué</span>;
    if (column.field === 'entity_id' && typeof value === 'string') return <span className="ay-cell-site">{display(column.field, row)}</span>;
    // Cellule bouton (ADR-100) : l'action de la vue pour la ligne, même condition, commande réautorisée.
    if (column.cell?.kind === 'button' && column.cell.action_id !== undefined) return <RowActionCell actionId={column.cell.action_id} row={row} />;
    if (column.cell?.kind === 'image_title' || column.cell?.kind === 'avatar') {
      const title = display(column.field, row);
      const subtitle = column.cell.subtitle_field === undefined ? undefined : display(column.cell.subtitle_field, row);
      if (title === '—' && (subtitle === undefined || subtitle === '—')) return '—';
      // Avatar (ADR-100) : tuile à initiales de couleur déterministe, la même pour un libellé partout.
      return (
        <span className={`ay-cell-imagetitle${column.cell.kind === 'avatar' ? ' ay-cell-imagetitle--avatar' : ''}`}>
          <Avatar label={title} src={image(column.cell.image_field ?? column.field, row)} size="sm" shape={column.cell.kind === 'avatar' ? 'circle' : 'rounded'} />
          <span className="ay-cell-imagetitle__text">
            <span className="ay-cell-imagetitle__title">{title}</span>
            {subtitle !== undefined && subtitle !== '—' && <span className="ay-cell-imagetitle__subtitle">{subtitle}</span>}
          </span>
        </span>
      );
    }
    if (value === null || value === undefined) return '—';
    if (column.cell?.kind === 'progress') {
      // Barre (ADR-100) : nombre ou mesure, jamais un montant (R2) ; ton warn puis danger au-delà des seuils.
      const raw = typeof value === 'object' && value !== null && 'value' in value ? (value as { value: unknown }).value : value;
      const number = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
      if (!Number.isFinite(number)) return display(column.field, row);
      const max = column.cell.max ?? 100;
      const tone = column.cell.danger_at !== undefined && number >= column.cell.danger_at ? 'danger' : column.cell.warn_at !== undefined && number >= column.cell.warn_at ? 'warn' : 'ok';
      return (
        <span className="ay-cell-progress">
          <span className={`ay-progress ay-progress--${tone}`} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={number}>
            <span style={{ width: `${Math.max(0, Math.min(100, (number / max) * 100))}%` }} />
          </span>
          <span className="ay-cell-progress__value">{display(column.field, row)}</span>
        </span>
      );
    }
    if (column.cell?.kind === 'chip') return <span className="ay-chip">{display(column.field, row)}</span>;
    // Statut : pastille au ton déclaré par la vue, libellé toujours visible ; sans ton, neutre (ADR-092).
    if (column.tones !== undefined || column.field === object?.state_field) {
      const tone = typeof value === 'string' ? column.tones?.[value] : undefined;
      return <Pill tone={TONE_TO_PILL[tone ?? 'neutral']}>{display(column.field, row)}</Pill>;
    }
    // Format déclaré par la colonne (1.7.0) ; un montant n'est jamais arrondi ici (R2).
    const field = fieldOf(object, column.field);
    const formatted = isAmountLike(field) && field?.type !== 'quantity' && field?.type !== 'number' ? undefined : formatColumnValue(column.format, value, field?.unit);
    return formatted ?? display(column.field, row);
  };

  const alignOf = (column: Column): 'start' | 'center' | 'end' => column.align ?? (isAmountLike(fieldOf(object, column.field)) ? 'end' : 'start');
  const classOf = (column: Column): string => {
    const field = fieldOf(object, column.field);
    const classes = [`ay-col--${alignOf(column)}`];
    if (isAmountLike(field) || field?.type === 'date' || field?.type === 'datetime' || ENVELOPE_SORTABLE.has(column.field)) classes.push('ay-col--mono');
    if (column.priority !== undefined) classes.push(`ay-col--p${column.priority}`);
    return classes.join(' ');
  };

  return { display, cell, image, alignOf, classOf };
}

/** Colonne implicite d'un champ (carte, Kanban, Agenda) : tons repris de la colonne de liste s'il y en a une. */
export function columnFor(fieldId: string, columns: readonly Column[]): Column {
  return columns.find((column) => column.field === fieldId) ?? { field: fieldId };
}
